import React, { memo } from 'react';
import { Select } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import {
  dspxGetChildrens,
  dspxGetWorkerChildrens,
} from '../../../../../../helpers/dspx-utils';
import { ReactComponent as RelatedFilterIcon } from '../../../../../../resource/RelatedFilterCard.svg';
import {
  hasFilterValue,
  selectPeopleActiveCount,
} from '../../../model/activity';
import { useRequestFilterCommands } from '../../../model/commandsContext';
import { requestFilterSectionIds } from '../../../model/sectionExpansion';
import { selectUserInfo } from '../../../model/selectors';
import { useFilterChange } from '../../../model/useFilterChange';
import FilterField from '../../primitives/FilterField';
import FilterSection from '../../primitives/FilterSection';
import { multiSelectTagProps } from '../../primitives/selectTagProps';
import styles from './PeopleFilterSection.module.scss';

const isAllTimeRange = (dateRange) => {
  if (!dateRange || !dateRange[0]) return false;
  const startDate = dateRange[0];
  if (!startDate || typeof startDate.year !== 'function') return false;
  const startYear = startDate.year();
  return startYear < 1500;
};

const selectPeopleFilterState = (state) => {
  const filter = state.filterStore;

  return {
    valueWorker: filter.valueWorker,
    valueExecuter: filter.valueExecuter,
    valueCreator: filter.valueCreator,
    valueStatus: filter.valueStatus,
    valueCompany: filter.valueCompany,
    dateRange: filter.dateRange,
    filterWorker: filter.filterWorker,
    filterExecuter: filter.filterExecuter,
    filterCreator: filter.filterCreator,
    filterStatus: filter.filterStatus,
    filterCompany: filter.filterCompany,
  };
};

const PeopleFilterSection = () => {
  const filterCommands = useRequestFilterCommands();
  const filter = useSelector(selectPeopleFilterState, shallowEqual);
  const userInfo = useSelector(selectUserInfo);
  const activeCount = useSelector(selectPeopleActiveCount);
  const { changeFilter } = useFilterChange();
  const canChangeCompany = userInfo?.ServiceCompanyFilter;

  return (
    <FilterSection
      sectionId={requestFilterSectionIds.people}
      heading="Компания и ответственные"
      description="Компания, статус и участники заявки"
      icon={<RelatedFilterIcon />}
      activeCount={activeCount}
    >
      <div className={styles.grid}>
        <FilterField
          label="Компания"
          active={hasFilterValue(filter.valueCompany)}
          required={isAllTimeRange(filter.dateRange) && (!filter.valueCompany || filter.valueCompany.length === 0)}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={canChangeCompany}
            placeholder="Выберите компанию"
            value={filter.valueCompany?.map(Number)}
            onChange={(value) => {
              if (!canChangeCompany && value.length === 0) return;
              changeFilter(filterCommands.applyCompany(value));
            }}
            optionFilterProp="searchtext"
            status={isAllTimeRange(filter.dateRange) && (!filter.valueCompany || filter.valueCompany.length === 0) ? 'error' : ''}
          >
            {dspxGetChildrens(filter.filterCompany, undefined, undefined, (el) => Number(el.id))}
          </Select>
        </FilterField>

        <FilterField
          label="Статус"
          active={hasFilterValue(filter.valueStatus)}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={true}
            placeholder="Выберите статус"
            value={filter.valueStatus}
            onChange={(value) => {
              changeFilter(filterCommands.applyStatus(value));
            }}
            optionFilterProp="searchtext"
          >
            {dspxGetChildrens(filter.filterStatus)}
          </Select>
        </FilterField>

        <FilterField
          label="Мастер"
          active={hasFilterValue(filter.valueWorker)}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={true}
            placeholder="Выберите мастера"
            value={filter.valueWorker}
            onChange={(value) => {
              changeFilter(filterCommands.applyWorker(value));
            }}
            optionLabelProp="label"
            optionFilterProp="searchtext"
          >
            {dspxGetWorkerChildrens(filter.filterWorker)}
          </Select>
        </FilterField>

        <FilterField
          label="Исполнитель"
          active={hasFilterValue(filter.valueExecuter)}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={true}
            placeholder="Выберите исполнителя"
            value={filter.valueExecuter}
            onChange={(value) => {
              changeFilter(filterCommands.applyExecuter(value));
            }}
            optionFilterProp="searchtext"
            optionLabelProp="label"
          >
            {dspxGetWorkerChildrens(filter.filterExecuter)}
          </Select>
        </FilterField>

        <FilterField
          label="Создатель"
          active={hasFilterValue(filter.valueCreator)}
          className={styles.fullWidth}
        >
          <Select
            {...multiSelectTagProps}
            mode="multiple"
            defaultActiveFirstOption={false}
            allowClear={true}
            placeholder="Выберите создателя"
            value={filter.valueCreator}
            onChange={(value) => {
              changeFilter(filterCommands.applyCreator(value));
            }}
            optionLabelProp="label"
            optionFilterProp="searchtext"
          >
            {dspxGetWorkerChildrens(filter.filterCreator, 23, false, true)}
          </Select>
        </FilterField>
      </div>
    </FilterSection>
  );
};

export default memo(PeopleFilterSection);
